import styled from "@emotion/styled"
import { PIcon, PRadioGroup, PRadioGroupOption, PTag } from "@porsche-design-system/components-react"
import type {
	TextColor,
	TextWeight,
	RadioGroupChangeEventDetail,
	TextSize,
} from "@porsche-design-system/components-react"
import {
	colorContrastMedium,
	fontWeightSemibold,
	spacingStaticSm,
	typescaleXs,
} from "@porsche-design-system/components-react/emotion"
import type { ForwardedRef, ReactElement } from "react"
import { forwardRef, Fragment, useCallback, useMemo } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { Tooltip } from "../../../tooltip/Tooltip"

export interface RadioOption<T extends string = string> {
	disabledHint?: string
	label: string
	message?: string | string[]
	tag?: string
	value: T
}

export interface RadioGroupProps<T extends string = string> {
	boldLabel?: boolean
	description?: string
	disabledHint?: string
	error?: string
	/**
	 * If set to true, the RadioButtons will be rendered horizontally. This only applies if there are less than 3 Options.
	 */
	horizontal?: boolean
	label?: string
	labelTooltip?: string
	name: string
	onChange?: (value: T) => void
	options: RadioOption<T>[]
	required?: boolean

	value?: T
}

const RadioGroupInner = <T extends string = string>(
	props: RadioGroupProps<T>,
	ref: ForwardedRef<HTMLInputElement>,
): ReactElement => {
	const { boldLabel, disabledHint, error, horizontal, label, labelTooltip, name, onChange, options, value, ...rest } =
		props

	const isHorizontal = useMemo(() => !!horizontal && options.length < 3, [horizontal, options])
	const hasCustomLabel = useMemo(() => label && !!(boldLabel ?? labelTooltip), [boldLabel, label, labelTooltip])

	const handleChange = useCallback(
		(e: CustomEvent<RadioGroupChangeEventDetail>) => {
			onChange?.((e.target as HTMLInputElement).value as T)
		},
		[onChange],
	)

	const group = (
		<PRadioGroup
			key={options.map((o) => o.value).join("-")}
			{...rest}
			name={name}
			label={hasCustomLabel ? undefined : label}
			direction={isHorizontal ? "row" : "column"}
			state={error ? "error" : "none"}
			message={error}
			value={value}
			disabled={!!disabledHint}
			onChange={handleChange}
		>
			{hasCustomLabel && (
				<LabelText
					slot="label"
					$weight={boldLabel ? "semibold" : undefined}
					$color={disabledHint ? "contrast-medium" : undefined}
				>
					{label}
					{labelTooltip && (
						<Tooltip content={labelTooltip} appendToBody>
							<PIcon name="information" />
						</Tooltip>
					)}
				</LabelText>
			)}
			{options.map((option) => {
				const hasMessageOrTag = !!(option.message || option.tag)

				return (
					<Tooltip
						key={option.value}
						content={option.disabledHint}
						appendToBody // without this, a div is appended within PRadioGroup causing an error to be thrown
					>
						<PRadioGroupOption
							ref={ref}
							label={hasMessageOrTag ? undefined : option.label}
							value={option.value}
							disabled={!!option.disabledHint}
						>
							{hasMessageOrTag && (
								<>
									<span slot="label">
										{option.label}
										{option.message && (
											<>
												<br />
												<LabelText $color="contrast-medium" $size="xs">
													{typeof option.message === "string"
														? option.message
														: option.message.map((msg, i) => (
																<Fragment key={i}>
																	{msg}
																	<br />
																</Fragment>
															))}
												</LabelText>
											</>
										)}
									</span>

									{option.tag && (
										<OptionTag slot="label-after" variant="primary" compact>
											{option.tag}
										</OptionTag>
									)}
								</>
							)}
						</PRadioGroupOption>
					</Tooltip>
				)
			})}
		</PRadioGroup>
	)

	return disabledHint ? (
		<Wrapper {...rest}>
			<Tooltip content={disabledHint}>{group}</Tooltip>
		</Wrapper>
	) : (
		group
	)
}

export const RadioGroup = forwardRef(RadioGroupInner) as (<T extends string = string>(
	props: RadioGroupProps<T> & { ref?: ForwardedRef<HTMLInputElement> },
) => ReactElement) & { displayName?: string }
RadioGroup.displayName = "RadioGroup"

const Wrapper = styled.div`
	display: inline-block;
	justify-self: start; /* to prevent full width element in flex or grid causing tooltip to appear in the middle */
`

const LabelText = styled("span", { shouldForwardProp })<{
	$color?: Extract<TextColor, "contrast-medium">
	$size?: Extract<TextSize, "xs">
	$weight?: Extract<TextWeight, "semibold">
}>`
	${({ $color }) => $color && `color: ${colorContrastMedium};`}
	${({ $weight }) => $weight && `font-weight: ${fontWeightSemibold};`}
	${({ $size }) => $size === "xs" && `font-size: ${typescaleXs};`}
`

const OptionTag = styled(PTag)`
	margin-inline-start: ${spacingStaticSm};
`
